"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { Megaphone, Pencil } from "lucide-react";
import { storeNoticeEmberShellClass } from "@/components/cart/seller-checkout-note-banner";
import { Button } from "@/components/ui/button";

type ProfileStoreNoticeProps = {
  note: string;
  isOwner: boolean;
  editDisabled?: boolean;
};

export function ProfileStoreNotice({
  note,
  isOwner,
  editDisabled = false,
}: ProfileStoreNoticeProps) {
  const t = useTranslations("profile.info");
  const text = note.trim();
  const canEdit = isOwner && !editDisabled;

  if (!canEdit && text.length === 0) {
    return null;
  }

  return (
    <div className={storeNoticeEmberShellClass("mb-4")}>
      <Megaphone
        className="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400"
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <span className="font-semibold text-sm text-amber-900 dark:text-amber-200">
            {t("storeNotice")}
          </span>
          {canEdit ? (
            <Button
              asChild
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-amber-600 hover:bg-amber-200/60 hover:text-amber-950 dark:text-amber-400 dark:hover:bg-amber-800/50 dark:hover:text-amber-50"
            >
              <Link href="/profile/edit" aria-label={t("editStoreNotice")}>
                <Pencil className="h-3.5 w-3.5" />
              </Link>
            </Button>
          ) : null}
        </div>
        {text.length > 0 ? (
          <p className="text-base whitespace-pre-wrap leading-snug text-amber-900/90 dark:text-amber-100/90">
            {text}
          </p>
        ) : canEdit ? (
          <Link
            href="/profile/edit"
            className="block text-base leading-snug text-amber-900/50 dark:text-amber-200/50 italic hover:text-amber-900/70 dark:hover:text-amber-200/70"
          >
            {t("addStoreNotice")}
          </Link>
        ) : null}
      </div>
    </div>
  );
}
